import axiosInstance from './api';

const getApiErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return (
    error.message ||
    error?.error?.message ||
    error?.data?.message ||
    error?.response?.data?.message ||
    fallback
  );
};

export const getPartnerGroupChannelHttpStatus = (error) => {
  const status = error?.status ?? error?.statusCode ?? error?.code;
  if (typeof status === 'number') return status;
  if (typeof status === 'string' && /^\d+$/.test(status)) return Number(status);
  return error?.response?.status ?? null;
};

/** GET /admin/partner/group-channel — partner org Stream group channel. */
export const getPartnerGroupChannel = async () => {
  try {
    const response = await axiosInstance.get('/admin/partner/group-channel');

    return {
      status: 'found',
      data: response.data,
      message: response.data?.message ?? null,
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 404) {
      return {
        status: 'not_found',
        data: null,
        message: getApiErrorMessage(error, 'No group channel for this partner organization.'),
      };
    }

    if (httpStatus === 403) {
      return {
        status: 'forbidden',
        data: null,
        message: getApiErrorMessage(
          error,
          'Partner access is required to view the community channel.',
        ),
      };
    }

    return {
      status: 'error',
      data: null,
      message: getApiErrorMessage(error, 'Failed to load community channel.'),
      error,
    };
  }
};

/** GET /admin/channel/:channelId/messages?limit=&next=&resolvedAt= */
export const getChannelMessages = async ({ limit = 20, next, channelId, resolvedAt } = {}) => {
  const normalizedChannelId = channelId ? String(channelId).trim() : '';
  if (!normalizedChannelId) {
    return {
      status: 'error',
      data: null,
      message: 'Channel id is required to load messages.',
    };
  }

  try {
    const params = { limit };
    if (next) params.next = next;
    const normalizedResolvedAt = resolvedAt ? String(resolvedAt).trim() : '';
    if (normalizedResolvedAt) {
      params.resolvedAt = normalizedResolvedAt;
    }

    const response = await axiosInstance.get(
      `/admin/channel/${encodeURIComponent(normalizedChannelId)}/messages`,
      { params },
    );

    return {
      status: 'found',
      data: response.data,
      message: response.data?.message ?? null,
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 404) {
      return {
        status: 'not_found',
        data: null,
        message: getApiErrorMessage(error, 'No group channel messages found.'),
      };
    }

    if (httpStatus === 403) {
      return {
        status: 'forbidden',
        data: null,
        message: getApiErrorMessage(
          error,
          'Partner access is required to view community messages.',
        ),
      };
    }

    return {
      status: 'error',
      data: null,
      message: getApiErrorMessage(error, 'Failed to load community messages.'),
      error,
    };
  }
};

/** GET /admin/partner/group-channel/join-requests — pending community join requests. */
export const getPartnerGroupChannelJoinRequests = async () => {
  try {
    const response = await axiosInstance.get('/admin/partner/group-channel/join-requests');

    return {
      status: 'found',
      data: response.data,
      message: response.data?.message ?? null,
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 404) {
      return {
        status: 'not_found',
        data: null,
        message: getApiErrorMessage(error, 'No join requests found for this community.'),
      };
    }

    if (httpStatus === 403) {
      return {
        status: 'forbidden',
        data: null,
        message: getApiErrorMessage(
          error,
          'Partner access is required to view community join requests.',
        ),
      };
    }

    return {
      status: 'error',
      data: null,
      message: getApiErrorMessage(error, 'Failed to load community join requests.'),
      error,
    };
  }
};

/** PUT /admin/partner/group-channel/join-requests/:requestId/approve */
export const approvePartnerGroupChannelJoinRequest = async (requestId) => {
  try {
    const response = await axiosInstance.put(
      `/admin/partner/group-channel/join-requests/${requestId}/approve`,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Join request approved successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to approve community join requests.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Join request not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to approve join request.'),
      error,
    };
  }
};

/** PUT /admin/partner/group-channel/join-requests/:requestId/decline */
export const declinePartnerGroupChannelJoinRequest = async (requestId, payload) => {
  try {
    const response = await axiosInstance.put(
      `/admin/partner/group-channel/join-requests/${requestId}/decline`,
      payload,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Join request declined successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to decline community join requests.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Join request not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to decline join request.'),
      error,
    };
  }
};

/** POST /admin/partner/group-channel — admin creates community for a partner org. */
export const createPartnerGroupChannel = async (payload) => {
  try {
    const response = await axiosInstance.post('/admin/partner/group-channel', payload);

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Community created successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Admin access is required to create the community channel.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Partner not found.'),
        error,
      };
    }

    if (httpStatus === 409) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Community already exists for this partner.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to create community channel.'),
      error,
    };
  }
};

/** PATCH /admin/partner/group-channel — update partner community details. */
export const updatePartnerGroupChannel = async (payload) => {
  try {
    const response = await axiosInstance.patch('/admin/partner/group-channel', payload);

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Community updated successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to update the community channel.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'No group channel for this partner organization.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update community channel.'),
      error,
    };
  }
};

/** POST /admin/partner/group-channel/archive — archive partner community channel. */
export const archivePartnerGroupChannel = async () => {
  try {
    const response = await axiosInstance.post('/admin/partner/group-channel/archive');

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Community archived successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to archive the community channel.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Community channel not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to archive community channel.'),
      error,
    };
  }
};

/** POST /admin/channel/:channelId/messages */
export const sendPartnerGroupChannelMessage = async ({
  messageText,
  attachments = [],
  channelId,
} = {}) => {
  const normalizedText = String(messageText ?? '').trim();
  const normalizedChannelId = channelId ? String(channelId).trim() : '';

  if (!normalizedChannelId) {
    return {
      success: false,
      message: 'Channel id is required to send a message.',
    };
  }

  if (!normalizedText) {
    return {
      success: false,
      message: 'Message text is required.',
    };
  }

  try {
    const response = await axiosInstance.post(
      `/admin/channel/${encodeURIComponent(normalizedChannelId)}/messages`,
      {
        messageText: normalizedText,
        attachments: Array.isArray(attachments) ? attachments : [],
      },
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Message sent',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to send community messages.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Community channel not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to send message.'),
      error,
    };
  }
};

/** POST /admin/channel/:channelId/messages/:messageId/reaction */
export const setPartnerGroupChannelMessageReaction = async (
  messageId,
  { type, enforce_unique = false, channelId } = {},
) => {
  const normalizedMessageId = String(messageId ?? '').trim();
  const normalizedChannelId = channelId ? String(channelId).trim() : '';
  const normalizedType = String(type ?? '')
    .trim()
    .toLowerCase();

  if (!normalizedChannelId) {
    return {
      success: false,
      message: 'Channel id is required to react.',
    };
  }

  if (!normalizedMessageId) {
    return {
      success: false,
      message: 'Message id is required to react.',
    };
  }

  if (!normalizedType) {
    return {
      success: false,
      message: 'Reaction type is required.',
    };
  }

  try {
    const response = await axiosInstance.post(
      `/admin/channel/${encodeURIComponent(normalizedChannelId)}/messages/${encodeURIComponent(normalizedMessageId)}/reaction`,
      { type: normalizedType, enforce_unique },
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Reaction updated successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to react to community messages.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Message not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update reaction.'),
      error,
    };
  }
};

/**
 * DELETE /admin/channel/:channelId/messages/:messageId/reaction/:reactionType
 */
export const removePartnerGroupChannelMessageReaction = async (
  messageId,
  { type, channelId } = {},
) => {
  const normalizedMessageId = String(messageId ?? '').trim();
  const normalizedChannelId = channelId ? String(channelId).trim() : '';
  const normalizedType = String(type ?? '')
    .trim()
    .toLowerCase();

  if (!normalizedChannelId) {
    return {
      success: false,
      message: 'Channel id is required to remove a reaction.',
    };
  }

  if (!normalizedMessageId) {
    return {
      success: false,
      message: 'Message id is required to remove a reaction.',
    };
  }

  if (!normalizedType) {
    return {
      success: false,
      message: 'Reaction type is required.',
    };
  }

  try {
    const response = await axiosInstance.delete(
      `/admin/channel/${encodeURIComponent(normalizedChannelId)}/messages/${encodeURIComponent(normalizedMessageId)}/reaction/${encodeURIComponent(normalizedType)}`,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Reaction removed successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to react to community messages.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Message not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to remove reaction.'),
      error,
    };
  }
};

/** PUT /admin/channel/:channelId/messages/:messageId */
export const updatePartnerGroupChannelMessage = async (
  messageId,
  { messageText, channelId } = {},
) => {
  const normalizedMessageId = String(messageId ?? '').trim();
  const normalizedChannelId = channelId ? String(channelId).trim() : '';
  const normalizedText = String(messageText ?? '').trim();

  if (!normalizedChannelId) {
    return {
      success: false,
      message: 'Channel id is required to edit a message.',
    };
  }

  if (!normalizedMessageId) {
    return {
      success: false,
      message: 'Message id is required to edit.',
    };
  }

  if (!normalizedText) {
    return {
      success: false,
      message: 'Message text is required.',
    };
  }

  try {
    const response = await axiosInstance.put(
      `/admin/channel/${encodeURIComponent(normalizedChannelId)}/messages/${encodeURIComponent(normalizedMessageId)}`,
      { messageText: normalizedText },
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Message updated successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to edit community messages.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Message not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to update message.'),
      error,
    };
  }
};

/** DELETE /admin/partner/group-channel/member/:memberId — remove a member from the partner community. */
export const removePartnerGroupChannelMember = async (memberId) => {
  const normalizedMemberId = String(memberId ?? '').trim();
  if (!normalizedMemberId) {
    return {
      success: false,
      message: 'Member id is required to block a community member.',
    };
  }

  try {
    const response = await axiosInstance.delete(
      `/admin/partner/group-channel/member/${normalizedMemberId}`,
    );

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Member removed successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to remove community members.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Member not found.'),
        error,
      };
    }

    if (httpStatus === 400) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Unable to remove this community member.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to remove community member.'),
      error,
    };
  }
};

/** POST /admin/partner/group-channel/restore — restore archived partner community channel. */
export const restorePartnerGroupChannel = async () => {
  try {
    const response = await axiosInstance.post('/admin/partner/group-channel/restore');

    return {
      success: true,
      data: response.data,
      message: response.data?.message ?? 'Community restored successfully',
    };
  } catch (error) {
    const httpStatus = getPartnerGroupChannelHttpStatus(error);

    if (httpStatus === 403) {
      return {
        success: false,
        message: getApiErrorMessage(
          error,
          'Partner access is required to restore the community channel.',
        ),
        error,
      };
    }

    if (httpStatus === 404) {
      return {
        success: false,
        message: getApiErrorMessage(error, 'Community channel not found.'),
        error,
      };
    }

    return {
      success: false,
      message: getApiErrorMessage(error, 'Failed to restore community channel.'),
      error,
    };
  }
};
